// File: frontend/src/components/meetings/CreateMeetingModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import './CreateMeetingModal.css';

// FIXED: Use environment variable or production URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 
  'https://z-poel.onrender.com/api/v1';

console.log('[CreateMeetingModal] Using API_BASE_URL:', API_BASE_URL);

const CreateMeetingModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    locationData: null,
    startDateTime: '',
    endDateTime: '',
    meetingType: 'in-person', // virtual, in-person, hybrid
    participants: [],
    // NEW: Add fields for meeting requests
    isRequest: false,
    recipientId: ''
  });

  // Location autocomplete state
  const [locationQuery, setLocationQuery] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounce timer for location search
  const [searchTimer, setSearchTimer] = useState(null);

  // Search for locations using Nominatim (OpenStreetMap)
  const searchLocation = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    setIsSearchingLocation(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'CommunityApp/1.0'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setLocationSuggestions(data.map(item => ({
          name: item.display_name,
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          type: item.type,
          address: item.address
        })));
        setShowLocationDropdown(true);
      }
    } catch (error) {
      console.error('Error searching location:', error);
    } finally {
      setIsSearchingLocation(false);
    }
  }, []);

  // Handle location input change with debounce
  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocationQuery(value);
    
    // Clear previous timer
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    // Set new timer for debounced search
    const timer = setTimeout(() => {
      searchLocation(value);
    }, 500);
    
    setSearchTimer(timer);
  };

  // Handle location selection from dropdown
  const handleLocationSelect = (location) => {
    setLocationQuery(location.name);
    setFormData({
      ...formData,
      location: location.name,
      locationData: {
        lat: location.lat,
        lng: location.lng,
        name: location.name,
        address: location.address
      }
    });
    setShowLocationDropdown(false);
    setLocationSuggestions([]);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    };
  }, [searchTimer]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        title: '',
        description: '',
        location: '',
        locationData: null,
        startDateTime: '',
        endDateTime: '',
        meetingType: 'in-person',
        participants: [],
        isRequest: false,
        recipientId: ''
      });
      setLocationQuery('');
      setLocationSuggestions([]);
      setShowLocationDropdown(false);
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Meeting title is required';
    }
    
    if (!formData.startDateTime) {
      newErrors.startDateTime = 'Start date & time is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (formData.endDateTime && formData.startDateTime) {
      const start = new Date(formData.startDateTime);
      const end = new Date(formData.endDateTime);
      if (end <= start) {
        newErrors.endDateTime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Determine which endpoint to use
      let endpoint, body;
      
      if (formData.isRequest) {
        // Meeting request to all users (broadcast)
        endpoint = `${API_BASE_URL}/meetings/request`;
        body = {
          title: formData.title,
          description: formData.description,
          location: formData.location,
          locationData: formData.locationData,
          proposedTime: formData.startDateTime,
          message: formData.description || `Open meeting request: ${formData.title}`,
          duration: formData.endDateTime ? 
            Math.round((new Date(formData.endDateTime) - new Date(formData.startDateTime)) / 60000) : 
            60,
          meetingType: formData.meetingType,
          broadcastType: 'all' // Broadcast to all users
        };
      } else {
        // Personal meeting
        endpoint = `${API_BASE_URL}/meetings`;
        body = {
          title: formData.title,
          description: formData.description,
          location: formData.location,
          locationData: formData.locationData,
          startDateTime: formData.startDateTime,
          endDateTime: formData.endDateTime,
          meetingType: formData.meetingType,
          participants: formData.participants
        };
      }
      
      console.log('[CreateMeetingModal] Submitting to:', endpoint);
      console.log('[CreateMeetingModal] Body:', body);
      
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      console.log('[CreateMeetingModal] Response status:', response.status);
      
      if (!response.ok) {
        let errorMessage = 'Failed to create meeting';
        try {
          const error = await response.json();
          errorMessage = error.message || error.error || errorMessage;
        } catch (e) {
          errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
      
      const result = await response.json();
      console.log('[CreateMeetingModal] Success:', result);
      
      // If onSubmit is provided, call it with the result
      if (onSubmit) {
        await onSubmit(result);
      }
      
      // Show success message
      if (formData.isRequest) {
        alert('✅ Meeting request sent successfully!\n\nAll users can see this request in their Incoming Requests panel.');
      } else {
        alert('✅ Meeting created successfully!');
      }
      
      // Close modal on success
      onClose();
      
      // Trigger refresh events
      window.dispatchEvent(new CustomEvent('newRequestCreated', { 
        detail: { type: 'meeting', meetingId: result.id } 
      }));
      
    } catch (error) {
      console.error('[CreateMeetingModal] Error:', error);
      setErrors({ submit: error.message || 'Failed to create meeting' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content create-meeting-modal">
        <div className="modal-header">
          <h2>Create New Meeting</h2>
          <button 
            className="close-btn" 
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {/* Meeting Request Toggle */}
          <div className="form-group" style={{
            background: '#f5f5f5',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="isRequest"
                checked={formData.isRequest}
                onChange={handleChange}
                style={{ marginRight: '10px' }}
              />
              <span style={{ fontWeight: '600' }}>
                Send as Open Meeting Request
              </span>
            </label>
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '12px', 
              color: '#666' 
            }}>
              {formData.isRequest ? 
                '✓ This will broadcast a meeting request to all users. Anyone can accept it.' :
                'This will create a personal meeting in your calendar'
              }
            </p>
          </div>

          {/* Meeting Title */}
          <div className="form-group">
            <label htmlFor="title">Meeting Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Coffee Meetup"
              className={errors.title ? 'error' : ''}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          {/* Meeting Type */}
          <div className="form-group">
            <label htmlFor="meetingType">Meeting Type</label>
            <select
              id="meetingType"
              name="meetingType"
              value={formData.meetingType}
              onChange={handleChange}
            >
              <option value="in-person">In-Person</option>
              <option value="virtual">Virtual</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          {/* Location with Autocomplete */}
          <div className="form-group location-group">
            <label htmlFor="location">Location *</label>
            <div className="location-input-wrapper">
              <input
                type="text"
                id="location"
                name="location"
                value={locationQuery}
                onChange={handleLocationInputChange}
                onFocus={() => locationSuggestions.length > 0 && setShowLocationDropdown(true)}
                placeholder="Start typing a location..."
                className={errors.location ? 'error' : ''}
                autoComplete="off"
              />
              {isSearchingLocation && (
                <span className="location-loading">🔍</span>
              )}
            </div>
            {errors.location && <span className="error-message">{errors.location}</span>}
            
            {/* Location Dropdown */}
            {showLocationDropdown && locationSuggestions.length > 0 && (
              <div className="location-dropdown">
                {locationSuggestions.map((location, index) => (
                  <div
                    key={index}
                    className="location-suggestion"
                    onClick={() => handleLocationSelect(location)}
                  >
                    <div className="location-icon">📍</div>
                    <div className="location-details">
                      <div className="location-name">{location.name}</div>
                      <div className="location-type">{location.type}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {formData.locationData && (
              <div className="selected-location">
                ✓ Selected: {formData.locationData.name}
              </div>
            )}
          </div>

          {/* Start Date & Time */}
          <div className="form-group">
            <label htmlFor="startDateTime">
              {formData.isRequest ? 'Proposed Date & Time *' : 'Start Date & Time *'}
            </label>
            <input
              type="datetime-local"
              id="startDateTime"
              name="startDateTime"
              value={formData.startDateTime}
              onChange={handleChange}
              min={new Date().toISOString().slice(0, 16)}
              className={errors.startDateTime ? 'error' : ''}
            />
            {errors.startDateTime && <span className="error-message">{errors.startDateTime}</span>}
          </div>

          {/* End Date & Time (not for requests) */}
          {!formData.isRequest && (
            <div className="form-group">
              <label htmlFor="endDateTime">End Date & Time (Optional)</label>
              <input
                type="datetime-local"
                id="endDateTime"
                name="endDateTime"
                value={formData.endDateTime}
                onChange={handleChange}
                min={formData.startDateTime}
                className={errors.endDateTime ? 'error' : ''}
              />
              {errors.endDateTime && <span className="error-message">{errors.endDateTime}</span>}
            </div>
          )}

          {/* Description */}
          <div className="form-group">
            <label htmlFor="description">
              {formData.isRequest ? 'Message (Optional)' : 'Description (Optional)'}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={formData.isRequest ? 
                "Add a message for the recipient..." : 
                "Add meeting details..."
              }
              rows="3"
            />
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="error-message submit-error" style={{
              background: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px'
            }}>
              {errors.submit}
            </div>
          )}

          {/* Action Buttons */}
          <div className="modal-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn-primary"
              disabled={isSubmitting}
              style={{
                background: formData.isRequest ? '#4CAF50' : '#2196F3'
              }}
            >
              {isSubmitting ? 
                'Creating...' : 
                (formData.isRequest ? 'Send Meeting Request' : 'Create Meeting')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateMeetingModal;
